import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import classNames from 'classnames';

import { dataSync } from 'apps/dataSync';
import { DEFAULT_METADATA_EXTENDED } from 'utils/metaData';
import { fetchDeliverySlot } from 'apps/deliverySlot/actions/deliverySlot';
import { fetchUserData } from 'apps/user/actions/user';
import { getRoutesProp } from 'utils/routing';

import BackToTopButton from 'apps/backToTopButton/BackToTopButton';
import BookSlotPrompt from 'apps/bookSlotPrompt/containers/BookSlotPrompt';
import CancelOrderChangesWarning from 'apps/header/containers/CancelOrderChangesWarning';
import CookieDisclaimer from 'apps/cookieDisclaimer/containers/CookieDisclaimer';
import EnforceAutologinParametersRemoved from 'apps/meta/containers/EnforceAutologinParametersRemoved';
import FeedbackPopup from 'apps/feedbackPopup/containers/FeedbackPopup';
import FoodProfilePopup from 'apps/foodProfile/containers/FoodProfilePopup';
import FlashMessages from 'apps/notifications/containers/FlashMessages';
import Footer from 'apps/footer/containers/Footer';
import GoogleTagManager from 'apps/gtm/containers/GoogleTagManager';
import CookieConsent from 'apps/cookieConsent/containers/CookieConsent';
import GraphicImpressionsContainer from 'apps/gtm/components/GraphicImpressionsContainer';
import GtmPageTypeProvider from 'apps/gtm/utils/gtmPageType/GtmPageTypeProvider';
import Header from 'apps/headerSwitch/HeaderSwitch';
import InactivityWarningContainer from 'apps/dataSync/containers/InactivityWarningContainer';
import UlpBanner from 'apps/ulp/containers/UlpBanner';
import PageStatisticsForRequestParams from 'apps/analytics/containers/PageStatisticsForRequestParams';
import PublicBasketApi from '[theme-lib]/apps/externalProducts/containers/PublicBasketApi';
import RoutingMetadata from 'utils/components/RoutingMetadata';
import SsoSessionSync from 'apps/login/containers/SsoSessionSync';
import SuperuserMsg from 'apps/superuserMsg/containers/SuperuserMsg';

import { DynamicYieldApiLoaderWrapper } from 'apps/dynamicYield';

export default class RootView extends PureComponent {
  static propTypes = {
    children: PropTypes.node,
    deliverySlot: PropTypes.object,
    isBookSlotPromptEnabled: PropTypes.bool,
    isDynamicYieldEnabled: PropTypes.bool,
    isMobileAppBannerVisible: PropTypes.bool,
    isOfflineAlertOpen: PropTypes.bool,
    isOspHeader: PropTypes.bool,
    isUnsupportedBrowserAlertOpen: PropTypes.bool,
    location: PropTypes.object,
    routes: PropTypes.arrayOf(PropTypes.object),
  };

  componentDidMount() {
    dataSync.appendAction('userProfileSync', fetchUserData, 20000);
    dataSync.appendAction('deliverySlotSync', fetchDeliverySlot, 20000);
  }

  componentWillUnmount() {
    dataSync.removeAction('userProfileSync');
    dataSync.removeAction('deliverySlotSync');
  }

  render() {
    const {
      isBookSlotPromptEnabled,
      isDynamicYieldEnabled,
      isMobileAppBannerVisible,
      isOfflineAlertOpen,
      isOspHeader,
      isUnsupportedBrowserAlertOpen,
      location,
      routes,
      oneTrustDomainId,
    } = this.props;
    const fullWidth = getRoutesProp(routes, 'layoutFullWidth', false);
    const noGap = getRoutesProp(routes, 'layoutNoGap', false);
    const withSidebarLeft = getRoutesProp(routes, 'layoutSidebarLeft', false);
    const withSidebarRight = getRoutesProp(routes, 'layoutSidebarRight', false);
    const withFooter = getRoutesProp(routes, 'layoutFooter', true);
    const isFeedbackPopupHidden = getRoutesProp(
      routes,
      'isFeedbackPopupHidden',
      false
    );
    const classNamesObject = {
      noGap,
      'mainContent--appBanner': isMobileAppBannerVisible,
      'mainContent--browserUnsupported': isUnsupportedBrowserAlertOpen,
      'mainContent--offlineAlert': isOfflineAlertOpen,
      'mainContent--withoutSidebar': !withSidebarLeft && !withSidebarRight,
      'mainContent--withSidebarLeft': withSidebarLeft,
      'mainContent--withSidebarRight': withSidebarRight,
    };

    return (
      <div
        id="root-view"
        className={classNames('gn-body', {
          'gn-body--fullWidth': fullWidth,
          'gn-body--ospHeader': isOspHeader,
        })}
      >
        <CookieConsent oneTrustDomainId={oneTrustDomainId} />
        <DynamicYieldApiLoaderWrapper isDynamicYieldEnabled={isDynamicYieldEnabled} />
        <Helmet {...DEFAULT_METADATA_EXTENDED} />
        <Header routes={routes} location={location} />
        <RoutingMetadata location={location} />
        <GtmPageTypeProvider
          gtmPageType={getRoutesProp(this.props.routes, 'gtmPageType')}
        >
          <div
            id="main-content"
            className={classNames('mainContent', classNamesObject)}
          >
            <GraphicImpressionsContainer location={location}>
              <UlpBanner />
              {this.props.children}
            </GraphicImpressionsContainer>
          </div>
        </GtmPageTypeProvider>
        <PageStatisticsForRequestParams />
        <Footer show={withFooter} />
        <CookieDisclaimer />
        <InactivityWarningContainer />
        <CancelOrderChangesWarning />
        <FlashMessages />
        <SuperuserMsg />
        <GoogleTagManager />
        <BackToTopButton />
        <PublicBasketApi />
        <SsoSessionSync location={location} />
        {isBookSlotPromptEnabled && <BookSlotPrompt />}
        <EnforceAutologinParametersRemoved />
        {!isFeedbackPopupHidden && <FeedbackPopup />}
        <FoodProfilePopup />
      </div>
    );
  }
}
